import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PrescriptionJourney } from "../components/PrescriptionJourney";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  ArrowRight, 
  Menu,
  X,
  Shield,
  FileCheck,
  Route,
  DollarSign,
  Package,
  ClipboardList,
  Stethoscope,
  Users,
  Building2,
  ChevronDown,
  Mail
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Intersection Observer Hook for scroll animations
const useInView = (options = {}) => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
      }
    }, { threshold: 0.1, ...options });

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []);

  return [ref, isInView];
};

// Animated Section Component
const AnimatedSection = ({ children, className = "", delay = 0 }) => {
  const [ref, isInView] = useInView();
  
  return (
    <div 
      ref={ref}
      className={`transition-all duration-700 ${className}`}
      style={{
        opacity: isInView ? 1 : 0,
        transform: isInView ? 'translateY(0)' : 'translateY(30px)',
        transitionDelay: `${delay}ms`
      }}
    >
      {children}
    </div>
  );
};

// Feature Card Component
const FeatureCard = ({ icon: Icon, title, delay = 0 }) => {
  const [ref, isInView] = useInView();
  
  return (
    <div 
      ref={ref}
      className="card-hover bg-white rounded-2xl p-6 border border-gray-100 shadow-sm"
      style={{
        opacity: isInView ? 1 : 0,
        transform: isInView ? 'translateY(0)' : 'translateY(20px)',
        transition: `all 0.5s ease ${delay}ms`
      }}
    >
      <div className="w-12 h-12 rounded-xl bg-[#FFD600] flex items-center justify-center mb-4">
        <Icon className="w-6 h-6 text-black" />
      </div>
      <h3 className="font-semibold text-gray-900 text-lg">{title}</h3>
    </div>
  );
};

// Stakeholder Card Component
const StakeholderCard = ({ icon: Icon, title, description, delay = 0 }) => {
  const [ref, isInView] = useInView();
  
  return (
    <div 
      ref={ref}
      className="card-hover bg-white rounded-2xl p-8 border border-gray-100 shadow-sm flex-1"
      style={{
        opacity: isInView ? 1 : 0,
        transform: isInView ? 'translateY(0)' : 'translateY(20px)',
        transition: `all 0.5s ease ${delay}ms`
      }}
    >
      <div className="w-14 h-14 rounded-2xl bg-[#FFD600] flex items-center justify-center mb-6">
        <Icon className="w-7 h-7 text-black" />
      </div>
      <h3 className="font-semibold text-gray-900 text-xl mb-3">{title}</h3>
      <p className="text-gray-600 leading-relaxed">{description}</p>
    </div>
  );
};

const LandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [formData, setFormData] = useState({
    full_name: "",
    clinic_email: "",
    organization_type: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/pilot-requests`, {
        full_name: formData.full_name,
        clinic_email: formData.clinic_email,
        clinic_state: "",
        primary_specialty: formData.organization_type,
        ehr_system: formData.message || "Briefing Request"
      });
      toast.success("Request submitted successfully! We'll be in touch soon.");
      setFormData({ full_name: "", clinic_email: "", organization_type: "", message: "" });
    } catch (error) {
      toast.error("Failed to submit request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setMobileMenuOpen(false);
  };

  const navLinks = [
    { label: "Home", id: "hero" },
    { label: "Solution", id: "solution" },
    { label: "Why RxAnywhere", id: "why" },
    { label: "For Investors", id: "vision" },
    { label: "Contact", id: "contact" }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'glass shadow-sm' : 'bg-transparent'
        }`}
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center" data-testid="logo">
              <img 
                src="https://customer-assets.emergentagent.com/job_clinical-router/artifacts/2pa6nkeo_image004.png" 
                alt="RxAnywhere Logo" 
                className="h-10 w-auto"
              />
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => scrollToSection(link.id)}
                  className="text-gray-600 hover:text-[#0A0A0A] transition-colors font-medium text-sm"
                >
                  {link.label}
                </button>
              ))}
            </div>

            {/* Desktop CTA */}
            <div className="hidden lg:block">
              <button
                onClick={() => scrollToSection("contact")}
                className="btn-primary text-sm"
                data-testid="nav-cta"
              >
                Request a Briefing
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-gray-900" />
              ) : (
                <Menu className="w-6 h-6 text-gray-900" />
              )}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="lg:hidden pt-4 pb-2 border-t border-gray-100 mt-4">
              <div className="flex flex-col gap-4">
                {navLinks.map((link) => (
                  <button
                    key={link.id}
                    onClick={() => scrollToSection(link.id)}
                    className="text-gray-600 hover:text-[#0A0A0A] transition-colors font-medium text-left"
                  >
                    {link.label}
                  </button>
                ))}
                <button
                  onClick={() => scrollToSection("contact")}
                  className="btn-primary text-sm w-fit mt-2"
                >
                  Request a Briefing
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section 
        id="hero"
        className="pt-32 pb-20 px-6 bg-white min-h-screen flex items-center"
        data-testid="hero-section"
      >
        <div className="max-w-7xl mx-auto w-full">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div>
              <AnimatedSection>
                <div className="inline-flex items-center px-6 py-3 rounded-full bg-[#FFD600] mb-6">
                  <span className="text-sm font-semibold text-black">Healthcare Infrastructure</span>
                </div>
              </AnimatedSection>
              
              <AnimatedSection delay={100}>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-medium text-gray-900 leading-tight mb-6">
                  Completing the{" "}
                  <span className="gradient-text">Prescription Process</span>
                </h1>
              </AnimatedSection>
              
              <AnimatedSection delay={200}>
                <p className="text-lg text-gray-600 leading-relaxed mb-4">
                  RxAnywhere is building the infrastructure that helps complete the prescription process.
                </p>
                <p className="text-gray-500 leading-relaxed mb-6">
                  After a provider prescribes medication, patients often encounter insurance requirements, 
                  prior authorizations, affordability challenges, pharmacy routing issues, and fulfillment delays. 
                  We're creating infrastructure designed to help improve and complete the prescription 
                  process—connecting providers, pharmacies, payers, and patients to reduce friction and 
                  improve medication access.
                </p>
                <p className="text-lg font-semibold text-gray-900 mb-8">
                  Because prescribing a medication should lead to patients receiving it.
                </p>
              </AnimatedSection>
              
              <AnimatedSection delay={300}>
                <div className="flex flex-wrap gap-4">
                  <button
                    onClick={() => scrollToSection("contact")}
                    className="btn-primary inline-flex items-center gap-2"
                  >
                    Request a Briefing
                    <ArrowRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => scrollToSection("problem")}
                    className="btn-secondary inline-flex items-center gap-2"
                  >
                    Learn More
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>
              </AnimatedSection>
            </div>

            {/* Right - Hero Image */}
            <AnimatedSection delay={400} className="hidden lg:block">
              <div className="relative">
                <div className="bg-white rounded-2xl p-2 shadow-2xl relative overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1758691463203-cce9d415b2b5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHw0fHxkb2N0b3IlMjB1c2luZyUyMHRhYmxldCUyMHBhdGllbnQlMjBjYXJlJTIwbWVkaWNhbHxlbnwwfHx8fDE3NzUwMDc1NzZ8MA&ixlib=rb-4.1.0&q=85"
                    alt="Healthcare providers using digital tools"
                    className="w-full h-auto rounded-xl"
                  />
                </div>
                <div className="absolute -bottom-4 -right-4 w-3/4 h-3/4 bg-[#FFD600] opacity-20 blur-3xl rounded-full -z-10"></div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section 
        id="problem"
        className="py-24 px-6 bg-[#F8FAFC]"
        data-testid="problem-section"
      >
        <div className="max-w-4xl mx-auto text-center">
          <AnimatedSection>
            <p className="section-badge inline-flex items-center px-7 py-3 rounded-full bg-black text-3xl font-semibold text-white mb-5">
              The Problem
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium text-gray-900 leading-tight mb-8">
              Healthcare has transformed how prescriptions are written,{" "}
              <span className="gradient-text">but not how they're completed.</span>
            </h2>
          </AnimatedSection>
          
          <AnimatedSection delay={200}>
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              While electronic prescribing made it possible to send prescriptions instantly, the journey 
              that follows remains fragmented. Insurance requirements, administrative workflows, pharmacy 
              coordination, and patient affordability continue to create obstacles that delay—or completely 
              prevent—patients from starting therapy.
            </p>
            <p className="text-gray-500 leading-relaxed">
              The result is unnecessary work for providers, frustration for patients, and billions of 
              dollars in avoidable healthcare inefficiencies.
            </p>
          </AnimatedSection>

          {/* Infographic */}
          <AnimatedSection delay={400}>
            <div className="mt-16 relative w-screen left-1/2 -translate-x-1/2 max-w-[1180px] px-6">
              <PrescriptionJourney />
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Solution Section */}
      <section 
        id="solution"
        className="py-24 px-6"
        data-testid="solution-section"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <AnimatedSection>
              <p className="section-badge inline-flex items-center px-7 py-3 rounded-full bg-black text-3xl font-semibold text-white mb-5">
                Our Solution
              </p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium text-gray-900 leading-tight mb-6">
                Infrastructure that helps{" "}
                <span className="gradient-text">complete the prescription process.</span>
              </h2>
            </AnimatedSection>
            
            <AnimatedSection delay={200}>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
                RxAnywhere is building an infrastructure layer that supports the prescription journey 
                after a prescription is written. Our platform is designed to help identify and resolve 
                barriers that interrupt medication access, creating a more connected process from 
                prescribing to patient fulfillment.
              </p>
              <p className="text-gray-900 font-semibold mt-4 max-w-2xl mx-auto">
                Rather than simply identifying where the process breaks, we're building infrastructure 
                that helps complete it.
              </p>
            </AnimatedSection>
          </div>

          {/* Feature Cards */}
          <div className="mb-20">
            <AnimatedSection>
              <h3 className="text-2xl font-medium text-gray-900 text-center mb-10">
                What RxAnywhere Helps Address
              </h3>
            </AnimatedSection>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <FeatureCard icon={FileCheck} title="Prior Authorization Challenges" delay={0} />
              <FeatureCard icon={Shield} title="Insurance & Coverage Barriers" delay={100} />
              <FeatureCard icon={Route} title="Prescription Routing" delay={200} />
              <FeatureCard icon={DollarSign} title="Patient Affordability" delay={300} />
              <FeatureCard icon={Package} title="Fulfillment Coordination" delay={400} />
              <FeatureCard icon={ClipboardList} title="Administrative Burden" delay={500} />
            </div>
          </div>
        </div>
      </section>

      {/* Why It Matters Section */}
      <section 
        id="why"
        className="py-24 px-6 bg-[#F8FAFC]"
        data-testid="why-section"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <AnimatedSection>
              <p className="section-badge inline-flex items-center px-7 py-3 rounded-full bg-black text-3xl font-semibold text-white mb-5">
                Why It Matters
              </p>
              <h2 className="text-3xl md:text-4xl font-medium text-gray-900 mb-4">
                Built for every stakeholder in the prescription journey
              </h2>
            </AnimatedSection>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <StakeholderCard 
              icon={Stethoscope} 
              title="Providers" 
              description="Spend less time navigating administrative obstacles and more time caring for patients."
              delay={0}
            />
            <StakeholderCard 
              icon={Users} 
              title="Patients" 
              description="Gain faster, simpler access to prescribed medications with fewer unnecessary delays."
              delay={200}
            />
            <StakeholderCard 
              icon={Building2} 
              title="Healthcare Organizations" 
              description="Improve operational efficiency, reduce workflow friction, and support better medication access."
              delay={400}
            />
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section 
        id="vision"
        className="py-24 px-6"
        data-testid="vision-section"
      >
        <div className="max-w-4xl mx-auto text-center">
          <AnimatedSection>
            <p className="section-badge inline-flex items-center px-7 py-3 rounded-full bg-black text-3xl font-semibold text-white mb-5">
              Our Vision
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium text-gray-900 leading-tight mb-8">
              Prescription access deserves the same innovation as{" "}
              <span className="gradient-text">electronic prescribing.</span>
            </h2>
          </AnimatedSection>
          
          <AnimatedSection delay={200}>
            <p className="text-lg text-gray-600 leading-relaxed mb-12">
              We believe prescription access should be as connected as prescribing itself. 
              RxAnywhere is building scalable healthcare infrastructure designed to improve and 
              complete one of the most important—and most fragmented—workflows in healthcare.
            </p>
          </AnimatedSection>

          <AnimatedSection delay={400}>
            <div className="relative bg-black rounded-3xl p-10 md:p-14 text-white">
              <div className="absolute inset-0 bg-[#FFD600]/5 rounded-3xl"></div>
              <p className="relative text-2xl md:text-3xl font-medium italic leading-relaxed">
                "Every prescription deserves the opportunity to become successful patient care."
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Contact Section */}
      <section 
        id="contact"
        className="py-24 px-6 bg-black"
        data-testid="contact-section"
      >
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <AnimatedSection>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium text-[#FFD600] leading-tight mb-6">
                Let's Start the Conversation
              </h2>
              <p className="text-lg text-gray-400 leading-relaxed max-w-2xl mx-auto">
                Whether you're a healthcare provider, health system, payer, pharmacy organization, 
                strategic partner, or investor, we'd welcome the opportunity to discuss the future 
                of prescription access.
              </p>
            </AnimatedSection>
          </div>

          <AnimatedSection delay={200}>
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 md:p-12 border border-white/10">
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Full Name</label>
                    <Input
                      type="text"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder:text-gray-500 focus:border-[#FFD600] focus:ring-1 focus:ring-[#FFD600]"
                      placeholder="Your name"
                      data-testid="input-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                    <Input
                      type="email"
                      name="clinic_email"
                      value={formData.clinic_email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder:text-gray-500 focus:border-[#FFD600] focus:ring-1 focus:ring-[#FFD600]"
                      placeholder="your@email.com"
                      data-testid="input-email"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Organization Type</label>
                  <Select onValueChange={(value) => handleSelectChange("organization_type", value)} value={formData.organization_type}>
                    <SelectTrigger className="w-full px-4 py-3 h-auto bg-white/10 border border-white/20 rounded-xl text-white focus:border-[#FFD600]">
                      <SelectValue placeholder="Select your organization type" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1E293B] border-white/20">
                      {["Healthcare Provider", "Health System", "Payer", "Pharmacy Organization", "Strategic Partner", "Investor", "Other"].map((type) => (
                        <SelectItem key={type} value={type} className="text-white hover:bg-white/10">
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Message (Optional)</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder:text-gray-500 focus:border-[#FFD600] focus:ring-1 focus:ring-[#FFD600] focus:outline-none"
                    placeholder="Tell us more about your interest..."
                    data-testid="input-message"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn-primary py-4 text-lg"
                  data-testid="submit-button"
                >
                  {isSubmitting ? "Submitting..." : "Request a Briefing"}
                </Button>
              </form>

              <div className="mt-8 pt-8 border-t border-white/10 text-center">
                <p className="text-gray-400 text-sm mb-2">Or email us directly at:</p>
                <a 
                  href="mailto:info@rxanywhereapp.com" 
                  className="text-[#FFD600] hover:text-[#E6C200] transition-colors font-medium inline-flex items-center gap-2"
                >
                  <Mail className="w-4 h-4" />
                  info@rxanywhereapp.com
                </a>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-white/10 py-12 px-6" data-testid="footer">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_clinical-router/artifacts/2pa6nkeo_image004.png" 
                alt="RxAnywhere Logo" 
                className="h-8 w-auto"
              />
            </div>
            
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-400">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => scrollToSection(link.id)}
                  className="hover:text-white transition-colors"
                >
                  {link.label}
                </button>
              ))}
            </div>
            
            <p className="text-sm text-gray-500">
              © 2026 RxAnywhere. All Rights Reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
